import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

const CoverageMap = lazy(() => import("@/components/LazyCoverageMap"));
import {
  Bus,
  FileText,
  Building2,
  Phone,
  Instagram,
  Facebook,
  MapPin,
  Clock,
  ShieldCheck,
  Zap,
  Users,
  
  Map as MapIcon,
  HeartHandshake,
  ArrowRight,
  Menu,
  X,
  
  Box,
  Car,
  Route as RouteIcon,

  
  CheckCircle2,
  FileCheck,
  Headset,
  Mail,
} from "lucide-react";
import logoAsset from "@/assets/logo-rne-v5.webp.asset.json";
import heroImg from "@/assets/hero-highway.webp";
import boxesImg from "@/assets/boxes.webp";
import vanImg from "@/assets/car-branded.webp";
import fleetImg from "@/assets/fleet-porto-velho.png.asset.json";


const WHATSAPP_NUMBER = "5569993650780";
const WHATSAPP_DISPLAY = "(69) 9 9365-0780";
const WHATSAPP_LINK = (msg?: string) =>
  msg ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}` : `https://wa.me/${WHATSAPP_NUMBER}`;

const HELP_SECTION_ID = "central-atendimento";
function scrollToHelp() {
  if (typeof window === "undefined") return;
  const el = document.getElementById(HELP_SECTION_ID);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  window.dispatchEvent(new CustomEvent("rne:highlight-help"));
}

/* Official WhatsApp glyph (brand mark) */
function WhatsAppIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/>
    </svg>
  );
}
/* Primary CTA — visually identical to the green WhatsApp button, but only scrolls
   to the Central de Atendimento section. Conversion happens exclusively there. */
function WhatsAppButton({ label, sub, icon: Icon = Headset, className = "" }: {
  msg?: string; label: string; sub?: string; size?: "md" | "sm"; className?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <button
      type="button"
      onClick={scrollToHelp}
      className={`group inline-flex max-w-full items-center gap-2.5 bg-[#0E2A55] hover:bg-[#13367A] border border-white/10 text-white pl-2 pr-5 py-2.5 rounded-2xl font-semibold transition-all duration-300 shadow-[0_10px_28px_-12px_rgba(11,42,85,0.55)] hover:shadow-[0_14px_32px_-12px_rgba(11,42,85,0.7)] hover:-translate-y-0.5 ${className}`}
    >
      <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="flex flex-col items-start leading-tight min-w-0">
        <span className="text-[14px] sm:text-[15px] truncate">{label}</span>
        {sub && <span className="text-[11px] sm:text-xs font-normal text-white/70 truncate">{sub}</span>}
      </span>
      <ArrowRight className="h-4 w-4 ml-0.5 shrink-0 transition-transform group-hover:translate-x-1" />
    </button>
  );
}


const FAQ_ITEMS = [
  { q: "Como envio uma encomenda?", a: "Entre em contato pelo WhatsApp, informe origem e destino, e a equipe agenda a coleta no mesmo dia." },
  { q: "Quais cidades são atendidas?", a: "Mais de 20 cidades em RO, AM e AC, ao longo da BR-364 e BR-319. Veja a lista completa na seção Cobertura." },
  { q: "Como comprar uma passagem?", a: "Fale com a equipe pelo WhatsApp, informando origem, destino e data da viagem." },
  { q: "Posso enviar documentos?", a: "Sim. Transportamos documentos e malotes com total confidencialidade e agilidade." },
  { q: "Qual o prazo de entrega?", a: "Na maioria dos trechos, a entrega é feita no mesmo dia. Confirme com a equipe ao solicitar." },
  { q: "Quais formas de pagamento?", a: "PIX, dinheiro e cartão (consultar disponibilidade por cidade)." },
];

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Rota Norte Express — BR-364 e BR-319" },
      {
        name: "description",
        content:
          "Transporte de passageiros e encomendas na BR-364 e BR-319. Vilhena, Porto Velho, Manaus e Rio Branco no mesmo dia. Atendimento pelo WhatsApp.",
      },
      { property: "og:title", content: "Rota Norte Express — BR-364 e BR-319" },
      {
        property: "og:description",
        content:
          "Passageiros e encomendas com segurança em RO, AM e AC. Vilhena, Porto Velho, Manaus e Rio Branco no mesmo dia.",
      },
      { property: "og:url", content: "https://rotanortexpress.com.br/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Rota Norte Express — BR-364 e BR-319" },
      { name: "twitter:description", content: "Passageiros e encomendas com segurança em RO, AM e AC. Atendimento pelo WhatsApp." },
    ],
    links: [
      { rel: "canonical", href: "https://rotanortexpress.com.br/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MovingCompany",
          name: "Rota Norte Express",
          description:
            "Transporte de passageiros e encomendas pela BR-364 e BR-319, atendendo Vilhena, Porto Velho, Manaus, Rio Branco e cidades do trajeto.",
          url: "https://rotanortexpress.com.br/",
          telephone: "+55 69 99365-0780",
          email: "contato@rotanortexpress.com.br",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Av. Rio Madeira, 3288 — Embratel",
            addressLocality: "Porto Velho",
            addressRegion: "RO",
            postalCode: "76820-408",
            addressCountry: "BR",
          },
          areaServed: [
            { "@type": "State", name: "Rondônia" },
            { "@type": "State", name: "Amazonas" },
            { "@type": "State", name: "Acre" },
          ],
          serviceType: [
            "Transporte de passageiros",
            "Envio de encomendas",
            "Transporte de documentos",
            "Atendimento corporativo",
          ],
          sameAs: ["https://www.instagram.com/rotanortexpress/", "https://www.facebook.com/rotanortexpress/"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
          })),
        }),
      },
    ],
  }),
});

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.classList.add("animate-fade-up");
          el.style.opacity = "1";
          io.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    el.style.opacity = "0";
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Index() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#F4F7FC] to-white text-foreground">
      <Navbar />
      <Hero />
      <TrustBar />
      <Services />
      <CoverageAndSteps />
      <Differentials />
      <HelpCenter />
      <FaqSection />
      <FinalCta />
      <Footer />
    </main>
  );
}

/* ---------- NAV ---------- */
function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<string>("#");
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#", label: "Início" },
    { href: "#servicos", label: "Serviços" },
    { href: "#cobertura", label: "Cobertura" },
    { href: "#diferenciais", label: "Diferenciais" },
    { href: "#faq", label: "Dúvidas" },
  ];

  useEffect(() => {
    const ids = links.map((l) => l.href.replace("#", "")).filter(Boolean);
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);

    const update = () => {
      if (window.scrollY < 120) {
        setActive("#");
        return;
      }
      const probe = window.innerHeight * 0.35;
      let current = "#";
      for (const sec of sections) {
        const rect = sec.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom >= probe) {
          current = `#${sec.id}`;
          break;
        }
      }
      setActive(current);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
        scrolled
          ? "bg-[#0B1C40] shadow-2xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8 h-20 lg:h-[80px] flex items-center justify-between gap-4">
        <a href="#" className="flex items-center shrink-0">
          <img src={logoAsset.url} alt="Rota Norte Express" width={150} height={100} decoding="async" fetchPriority="high" className="h-11 sm:h-12 lg:h-14 w-auto" />
        </a>
        <nav className="hidden lg:flex items-center gap-6 xl:gap-7 flex-1 justify-center">
          {links.map((l) => {
            const isActive = active === l.href;
            return (
              <a
                key={l.label}
                href={l.href}
                aria-current={isActive ? "page" : undefined}
                className={`text-[15px] font-medium transition-colors relative after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-0.5 after:rounded-full after:transition-all after:duration-300 ${
                  isActive
                    ? "text-white after:bg-white after:opacity-100 after:scale-x-100"
                    : "text-white/75 hover:text-white after:bg-white after:opacity-0 after:scale-x-0"
                }`}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={scrollToHelp}
          className="hidden lg:inline-flex items-center gap-2 bg-white text-brand-dark hover:bg-white/90 px-5 py-2 rounded-full text-[13px] font-semibold transition-all shadow-sm hover:shadow-md shrink-0"
        >
          <Headset className="h-4 w-4" />
          Solicitar orçamento
        </button>
        <button
          type="button"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div id="mobile-nav" className="lg:hidden bg-[#0B1C40] border-t border-white/10 animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-white/80 hover:text-white hover:bg-white/5 rounded-md transition-colors"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => { setOpen(false); scrollToHelp(); }}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-white text-brand-dark hover:bg-white/90 px-5 py-3 rounded-full text-sm font-semibold transition-all"
            >
              <Headset className="h-4 w-4" />
              Solicitar orçamento
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  const route364: Array<{ c: string; s: string; hl?: "start" | "end" }> = [
    { c: "Vilhena", s: "RO", hl: "start" },
    { c: "Guaporé", s: "RO" },
    { c: "Pimenta Bueno", s: "RO" },
    { c: "Cacoal", s: "RO" },
    { c: "Presidente Médici", s: "RO" },
    { c: "Ji-Paraná", s: "RO" },
    { c: "Ouro Preto", s: "RO" },
    { c: "Jaru", s: "RO" },
    { c: "Ariquemes", s: "RO" },
    { c: "São Pedro", s: "RO" },
    { c: "Itapoã do Oeste", s: "RO" },
    { c: "Candeias do Jamari", s: "RO" },
    { c: "Porto Velho", s: "RO" },
    { c: "Jaci Paraná", s: "RO" },
    { c: "Nova Mutum", s: "RO" },
    { c: "Jirau", s: "RO" },
    { c: "Abunã", s: "RO" },
    { c: "Vista Alegre do Abunã", s: "RO" },
    { c: "Extrema", s: "RO" },
    { c: "Nova Califórnia", s: "RO" },
    { c: "Vila Campina", s: "AC" },
    { c: "Rio Branco", s: "AC", hl: "end" },
  ];

  const route319: Array<{ c: string; s: string; hl?: "start" | "end" }> = [
    { c: "Porto Velho", s: "RO", hl: "start" },
    { c: "Humaitá", s: "AM" },
    { c: "Distrito de Realidade", s: "AM" },
    { c: "Careiro", s: "AM" },
    { c: "Vila Rio Araça", s: "AM" },
    { c: "Manaus", s: "AM", hl: "end" },
  ];





  return (
    <section className="relative bg-[#040D22] text-white overflow-hidden pt-24 min-h-screen flex flex-col">
      {/* Background van image */}
      <div className="absolute inset-0">
        <img
          src={heroImg}
          alt="Van executiva da Rota Norte Express na BR-364 e BR-319 ao entardecer"
          className="absolute inset-0 w-full h-full object-cover object-right opacity-95"
          width={1600}
          height={1067}
          fetchPriority="high"
          decoding="async"
        />

        {/* Layered gradients for depth and legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#040D22] via-[#06122B]/90 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040D22] via-transparent to-[#040D22]/60" />

        {/* Subtle red brand glow from the right */}
        <div
          className="absolute inset-0 opacity-70 mix-blend-screen pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 55% at 85% 40%, rgba(220,38,38,0.22), transparent 60%)",
          }}
        />

        {/* Cool blue ambient light from the left */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 60% at 10% 30%, rgba(37,99,235,0.18), transparent 65%)",
          }}
        />

        {/* Fine grid texture */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 80%)",
          }}
        />

        {/* Vignette bottom for section transition */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#040D22]" />

        {/* Film grain */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-14">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-4 xl:gap-6 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-7 animate-fade-up max-w-[640px] mx-auto lg:mx-0 text-center lg:text-left">
            <span className="inline-flex items-center gap-2 bg-[#0E2A55]/70 backdrop-blur-md border border-brand-light/30 rounded-full px-4 py-2 text-xs sm:text-sm font-medium text-white">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand-red opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-red" />
              </span>
              Passageiros e encomendas · BR-364 e BR-319
            </span>
            <h1 className="mt-5 font-display text-[36px] sm:text-[48px] lg:text-[56px] xl:text-[64px] font-extrabold leading-[1.05] tracking-tight text-white">
              Vilhena, Porto Velho, Manaus e Rio Branco{" "}
              <span className="relative inline-block text-white">
                no mesmo dia
                <span className="absolute left-0 right-0 -bottom-1 h-1.5 rounded-full bg-brand-red" />
              </span>
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg lg:text-xl text-white/80 max-w-[580px] mx-auto lg:mx-0 leading-relaxed">
              Transporte de <strong className="text-white">passageiros</strong> e <strong className="text-white">encomendas</strong> com saídas diárias pela BR-364 e BR-319. Quem conhece a estrada leva você e o que é seu com segurança, pontualidade e atendimento direto pelo WhatsApp.
            </p>




            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 items-center justify-center lg:justify-start">
              <WhatsAppButton
                icon={Headset}
                label="Falar com um especialista"
                sub="Atendimento humano em minutos"
              />
              <a
                href="#cobertura"
                className="group inline-flex max-w-full items-center gap-2.5 bg-[#0E2A55]/70 hover:bg-[#13367A] backdrop-blur-md border border-white/15 text-white pl-2 pr-5 py-2.5 rounded-2xl font-semibold transition-all duration-300 hover:-translate-y-0.5"
              >
                <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-[18px] w-[18px]" />
                </span>
                <span className="flex flex-col items-start leading-tight min-w-0">
                  <span className="text-[14px] sm:text-[15px] truncate">Ver rotas atendidas</span>
                  <span className="text-[11px] sm:text-xs font-normal text-white/70 truncate">Cidades e horários</span>
                </span>
                <ArrowRight className="h-4 w-4 ml-0.5 shrink-0 transition-transform group-hover:translate-x-1" />
              </a>
            </div>


            <div className="mt-10 sm:mt-12 grid grid-cols-2 lg:grid-cols-4 max-w-2xl mx-auto lg:mx-0 gap-x-3 gap-y-6">
              {[
                { i: Clock, t: "Atendimento rápido" },
                { i: ShieldCheck, t: "Segurança garantida" },
                { i: MapPin, t: "BR-364 e BR-319" },
                { i: Users, t: "Equipe experiente" },
              ].map(({ i: Icon, t }, idx) => (
                <div
                  key={t}
                  className={`flex flex-col items-center text-center gap-2 text-white/90 px-1.5 min-w-0 ${
                    idx > 0 ? "lg:border-l lg:border-white/10" : ""
                  }`}
                >
                  <span className="h-9 w-9 rounded-full border border-white/25 flex items-center justify-center shrink-0">
                    <Icon className="h-[15px] w-[15px] text-white" />
                  </span>
                  <span className="text-[12px] sm:text-[12.5px] font-semibold leading-snug break-words hyphens-auto w-full">
                    {t}
                  </span>
                </div>
              ))}
            </div>




          </div>

          {/* Right: route cards */}
          <div className="lg:col-span-5 animate-scale-in space-y-5">
            {[
              { tag: "Rota 1 · BR-364", title: "Vilhena → Rio Branco", sub: "Passando por Porto Velho", data: route364 },
              { tag: "Rota 2 · BR-319", title: "Porto Velho → Manaus", sub: "Conexão direta com o Amazonas", data: route319 },
            ].map((card, cardIdx) => {
              const n = card.data.length;
              const step = 32; // px per city row
              const segs = n - 1;
              const goSpan = 46; // %
              const rotSpan = 4; // %
              const retSpan = 46; // %
              const segGo = goSpan / segs;
              const segRet = retSpan / segs;
              const dwellRatio = 0.45;
              const animName = `route-car-${cardIdx}`;
              const trailName = `route-trail-${cardIdx}`;
              const frames: string[] = [];
              const trailFrames: string[] = [];
              trailFrames.push(`0% { height: 0px; top: 16px; }`);
              // Going down
              for (let i = 0; i < n; i++) {
                const arrive = i * segGo;
                const y = i * step;
                frames.push(`${arrive.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(0deg); }`);
                if (i > 0) trailFrames.push(`${arrive.toFixed(2)}% { height: ${y}px; top: 16px; }`);
                if (i < n - 1) {
                  const leave = arrive + segGo * dwellRatio;
                  frames.push(`${leave.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(0deg); }`);
                  if (i > 0) trailFrames.push(`${leave.toFixed(2)}% { height: ${y}px; top: 16px; }`);
                }
              }
              // Rotate at end
              const endY = segs * step;
              frames.push(`${(goSpan + rotSpan).toFixed(2)}% { transform: translate(-50%, ${endY}px) rotate(180deg); }`);
              trailFrames.push(`${(goSpan + rotSpan).toFixed(2)}% { height: ${endY}px; top: 16px; }`);
              // Reset trail to start from bottom for return
              const retStart = goSpan + rotSpan;
              trailFrames.push(`${(retStart + 0.01).toFixed(2)}% { height: 0px; top: ${16 + endY}px; }`);
              // Returning up
              for (let k = 1; k <= n - 1; k++) {
                const i = n - 1 - k;
                const arrive = retStart + k * segRet;
                const y = i * step;
                frames.push(`${arrive.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(180deg); }`);
                const trailH = (segs - i) * step;
                trailFrames.push(`${arrive.toFixed(2)}% { height: ${trailH}px; top: ${16 + i * step}px; }`);
                if (k < n - 1) {
                  const leave = arrive + segRet * dwellRatio;
                  frames.push(`${leave.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(180deg); }`);
                  trailFrames.push(`${leave.toFixed(2)}% { height: ${trailH}px; top: ${16 + i * step}px; }`);
                }
              }
              frames.push(`100% { transform: translate(-50%, 0px) rotate(360deg); }`);
              trailFrames.push(`100% { height: ${endY}px; top: 16px; }`);
              const duration = Math.max(28, n * 4);
              return (
              <div key={card.tag} className="bg-[#0B1C40]/75 backdrop-blur-2xl rounded-3xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.75)] p-6 sm:p-7 border border-white/15">
                <style>{`@keyframes ${animName} { ${frames.join(" ")} } @keyframes ${trailName} { ${trailFrames.join(" ")} }`}</style>
                <div className="flex items-center gap-2.5">
                  <span className="h-9 w-9 rounded-full bg-brand-light/15 border border-brand-light/30 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-brand-light" />
                  </span>
                  <div className="text-[11px] font-semibold text-white/60 uppercase tracking-[0.22em]">
                    {card.tag}
                  </div>
                </div>
                <h2 className="mt-3 font-display text-[22px] sm:text-[24px] font-extrabold text-white leading-tight">
                  {card.title}
                </h2>
                <p className="mt-1 text-[12px] text-white/55 uppercase tracking-[0.18em] font-semibold">
                  {card.sub}
                </p>

                <div className="mt-4 space-y-0 relative">
                  {card.data.map((p, i, arr) => (
                    <div key={p.c} className="flex items-stretch gap-3.5 h-8">
                      <div className="flex flex-col items-center w-4 shrink-0 relative">
                        {i > 0 && <div className="w-px flex-1 bg-white/20" />}
                        <div
                          className={
                            p.hl === "start"
                              ? "w-3 h-3 rounded-full border-2 border-[color:var(--whatsapp)] bg-transparent shadow-[0_0_0_3px_rgba(37,211,102,0.15)] shrink-0"
                              : p.hl === "end"
                              ? "w-3 h-3 rounded-full bg-brand-light ring-[3px] ring-brand-light/30 shrink-0"
                              : "w-2 h-2 rounded-full border border-white/45 bg-transparent shrink-0"
                          }
                        />
                        {i < arr.length - 1 && <div className="w-px flex-1 bg-white/20" />}
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-white font-medium text-[13.5px]">{p.c}</span>
                        <span className="text-[10.5px] text-white/45 tracking-wider">{p.s}</span>
                      </div>
                    </div>
                  ))}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute rounded-full bg-gradient-to-b from-brand-light to-[color:var(--whatsapp)] shadow-[0_0_10px_rgba(66,165,245,0.6)]"
                    style={{
                      left: "7px",
                      width: "2px",
                      top: "16px",
                      height: "0px",
                      animation: `${trailName} ${duration}s linear infinite`,
                      willChange: "height, top",
                    }}
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute flex items-center justify-center drop-shadow-[0_4px_10px_rgba(66,165,245,0.55)]"
                    style={{
                      left: "8px",
                      top: "2px",
                      animation: `${animName} ${duration}s linear infinite`,
                      willChange: "transform",
                    }}
                  >
                    <svg viewBox="0 0 22 36" className="h-7 w-[18px]" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="2" width="16" height="32" rx="4" fill="#0B1C40" stroke="#42A5F5" strokeWidth="1" />
                      <rect x="5" y="5" width="12" height="6" rx="1.5" fill="#FFD66B" />
                      <rect x="5" y="14" width="12" height="9" rx="1.5" fill="#1E3A8A" />
                      <rect x="5" y="26" width="12" height="5" rx="1.5" fill="#0B1C40" stroke="#42A5F5" strokeWidth="0.6" />
                      <rect x="1.5" y="7" width="2" height="5" rx="0.6" fill="#0B1C40" />
                      <rect x="18.5" y="7" width="2" height="5" rx="0.6" fill="#0B1C40" />
                      <rect x="1.5" y="24" width="2" height="5" rx="0.6" fill="#0B1C40" />
                      <rect x="18.5" y="24" width="2" height="5" rx="0.6" fill="#0B1C40" />
                    </svg>
                  </span>

                </div>
              </div>
              );
            })}

          </div>
        </div>
      </div>
    </section>

  );
}

function TrustBar() {
  const items = [
    { i: MapPin, n: "+20", t: "Cidades atendidas" },
    { i: Bus, n: "Passageiros e encomendas", t: "Na mesma rota" },
    { i: MapIcon, n: "BR-364 e BR-319", t: "Até Manaus" },
    { i: WhatsAppIcon, n: "Atendimento rápido", t: "Pelo WhatsApp", green: true },
  ];
  return (
    <section className="mt-10 sm:mt-14 lg:mt-16 relative z-10">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(7,28,58,0.35)] border border-border overflow-hidden">
          {items.map(({ i: Icon, n, t, green }, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-4 px-5 sm:px-6 py-6 sm:py-7 transition-colors hover:bg-surface/60 min-w-0 ${
                idx > 0 ? "sm:[&:nth-child(even)]:border-l sm:border-border lg:border-l lg:[&:nth-child(odd)]:border-l" : ""
              } ${idx >= 2 ? "sm:border-t lg:border-t-0" : ""}`}
            >
              <span
                className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full flex items-center justify-center shrink-0 ${
                  green ? "bg-[color:var(--whatsapp)] text-white shadow-[0_10px_24px_-6px_rgba(37,211,102,0.6)]" : "bg-brand-dark/5 text-brand-dark"
                }`}
              >
                <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
              </span>
              <div className="leading-tight min-w-0">
                <div className="font-display font-extrabold text-brand-dark text-[14px] sm:text-[16px] break-words">{n}</div>
                <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-muted-foreground mt-1 break-words">
                  {t}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- SERVICES ---------- */
function Services() {
  const ref = useReveal<HTMLDivElement>();
  const services = [
    {
      icon: Bus,
      title: "Transporte de Passageiros",
      desc: "Viagens diárias com conforto, segurança e pontualidade pela BR-364 e BR-319.",
    },
    {
      icon: Box,
      title: "Envio de Encomendas",
      desc: "Encomendas com agilidade e segurança, com entregas para diversas cidades.",
    },
    {
      icon: FileText,
      title: "Transporte de Documentos",
      desc: "Documentos e malotes com entregas rápidas e total confidencialidade.",
    },
    {
      icon: Building2,
      title: "Atendimento para Empresas",
      desc: "Soluções logísticas para empresas, lojas, escritórios e e-commerces.",
    },
  ];
  return (
    <section id="servicos" className="py-20 sm:py-24">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
          <span className="eyebrow">O que fazemos</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
            Nossos serviços
          </h2>
          <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
            Soluções completas de transporte para pessoas e empresas na BR-364 e BR-319.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {services.map((s, i) => (
            <button
              key={s.title}
              type="button"
              onClick={scrollToHelp}
              className="group relative text-left w-full bg-white rounded-3xl p-7 border border-border shadow-card hover:shadow-[0_30px_60px_-20px_rgba(11,60,136,0.35)] hover:border-brand-light/50 transition-shadow duration-300 overflow-hidden"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-brand-light/0 group-hover:bg-brand-light/10 transition-colors duration-500" />
              <div className="absolute top-4 right-4 h-7 w-7 rounded-full bg-brand-light/10 border border-brand-light/30 flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="h-3.5 w-3.5 text-brand-light" />
              </div>
              <div className="relative w-14 h-14 rounded-2xl bg-brand-dark/5 text-brand-dark group-hover:bg-brand-dark group-hover:text-white flex items-center justify-center transition-colors duration-300">
                <s.icon className="h-7 w-7" />
              </div>

              <h3 className="relative mt-6 font-display text-[17px] sm:text-lg font-bold text-brand-dark leading-snug">{s.title}</h3>
              <p className="relative mt-2.5 text-[14px] text-muted-foreground leading-relaxed">{s.desc}</p>
              <div className="relative mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-light opacity-0 group-hover:opacity-100 group-hover:gap-3 transition-all">
                Solicitar atendimento <ArrowRight className="h-4 w-4" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- COVERAGE + HOW IT WORKS ---------- */
function CoverageAndSteps() {
  const steps = [
    { i: WhatsAppIcon, t: "Entre em contato", d: "Fale conosco pelo WhatsApp e tire suas dúvidas." },
    { i: FileCheck, t: "Informe origem e destino", d: "Passageiro ou encomenda, nossa equipe orienta." },
    { i: Bus, t: "Embarque ou coleta", d: "Buscamos sua encomenda ou realizamos seu embarque." },
    { i: CheckCircle2, t: "Entrega realizada", d: "Sua encomenda chega com segurança e pontualidade." },
  ];

  const stops = [
    { c: "Manaus", s: "AM", x: 70, y: 60 },
    { c: "Porto Velho", s: "RO", x: 130, y: 150 },
    { c: "Ariquemes", s: "RO", x: 175, y: 195 },
    { c: "Ji-Paraná", s: "RO", x: 215, y: 260 },
    { c: "Cacoal", s: "RO", x: 255, y: 310 },
    { c: "Vilhena", s: "RO", x: 310, y: 365 },
  ];

  return (
    <section id="cobertura" className="w-full max-w-full overflow-hidden py-20 sm:py-24">
      <div className="mx-auto grid w-full max-w-[1320px] min-w-0 grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8">
        {/* Coverage */}
        <div className="w-full max-w-full min-w-0 text-center">
          <span className="eyebrow">Onde atendemos</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
            Área de cobertura
          </h2>
          <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
            Atendemos todas as cidades ao longo da BR-364 e BR-319, de Vilhena a Manaus e também até Rio Branco/AC.
          </p>
          <div className="relative mt-6 w-full max-w-full min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-[#06122B] shadow-card">
            <div className="flex min-w-0 items-center justify-between gap-3 px-4 pt-4 pb-2">
              <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-brand-light uppercase">
                <MapIcon className="h-3.5 w-3.5" /> Mapa de cobertura
              </span>
              <span className="shrink-0 text-[11px] font-semibold text-white/60">RO • AM • AC</span>
            </div>
            <div className="w-full max-w-full min-w-0 overflow-hidden px-4 pb-4">
              <Suspense fallback={<div className="w-full h-[460px] rounded-2xl bg-[#0A1E40] animate-pulse" />}>
                <CoverageMap />
              </Suspense>
            </div>

          </div>
        </div>


        {/* How it works */}
        <div className="min-w-0 text-center">
          <span className="eyebrow">Processo</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
            Como funciona
          </h2>
          <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">Simples, rápido e eficiente — em quatro passos.</p>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 relative">
            {/* connecting line behind icons */}
            <div className="hidden sm:block absolute top-9 left-[12.5%] right-[12.5%] h-px border-t-2 border-dashed border-brand-light/30" aria-hidden />
            {steps.map((s, i) => (
              <div key={s.t} className="relative text-center">
                <div className="relative mx-auto w-fit">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-1 bg-brand-dark text-white text-[11px] font-bold rounded-full w-6 h-6 flex items-center justify-center z-10 shadow-md">
                    {i + 1}
                  </div>
                  <div
                    className={`mx-auto w-[72px] h-[72px] rounded-full flex items-center justify-center border-2 transition-colors ${
                      i === steps.length - 1
                        ? "bg-[color:var(--whatsapp)] border-[color:var(--whatsapp)] text-white shadow-[0_10px_24px_-8px_rgba(37,211,102,0.55)]"
                        : "bg-white border-border text-brand-dark shadow-card"
                    }`}
                  >
                    <s.i className="h-7 w-7" />
                  </div>
                </div>
                <h3 className="mt-4 font-display text-sm font-bold text-brand-dark">{s.t}</h3>
                <p className="mt-1 text-xs text-muted-foreground leading-snug">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <WhatsAppButton
              icon={CheckCircle2}
              label="Começar minha solicitação"
              sub="Resposta rápida da nossa equipe"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- HELP CENTER ---------- */
const MESSAGE_ENCOMENDA = `Olá, tenho interesse em enviar uma encomenda pela Rota Norte Express.`;

const MESSAGE_PASSAGEM = `Olá, tenho interesse em comprar uma passagem pela Rota Norte Express.`;

function HelpCard({
  icon: Icon, title, text, benefits, label, msg, delay, image, imageAlt, imageClass, footer,
}: {
  icon: typeof Box; title: string; text: string; benefits: string[];
  label: string; msg: string; delay: string;
  image: string; imageAlt: string; imageClass?: string; footer: string;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{ animationDelay: delay }}
      className="group relative flex flex-col bg-white rounded-[30px] border border-[color:var(--border)] shadow-card overflow-hidden transition-shadow duration-300 hover:shadow-card-hover"
    >
      {/* top-left blue bar */}
      <span aria-hidden className="absolute top-0 left-8 h-1.5 w-24 rounded-b-full bg-gradient-to-r from-brand-dark to-brand-light" />

      {/* mobile / tablet banner image */}
      <div className="lg:hidden relative h-64 sm:h-72 w-full overflow-hidden">
        <img
          src={image}
          alt={imageAlt}
          loading="lazy"
          decoding="async"
          className={`h-full w-full object-cover object-center ${imageClass ?? ""}`}
        />
      </div>

      <div className="relative p-10 sm:p-14 pb-7 flex-1 flex flex-col">
        {/* desktop right blue circle with image */}
        <div aria-hidden className="pointer-events-none absolute -right-10 xl:-right-14 top-12 hidden lg:block">
          <div className="relative h-[240px] w-[240px] xl:h-[270px] xl:w-[270px]">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-light/25 via-brand-light/10 to-transparent" />
            <div className="absolute inset-3 rounded-full overflow-hidden ring-1 ring-brand-light/20 shadow-[0_18px_40px_-18px_rgba(11,36,71,0.35)] bg-[#0B3C88]">
              <img
                src={image}
                alt={imageAlt}
                loading="lazy"
                decoding="async"
                className={`h-full w-full object-cover object-center ${imageClass ?? ""}`}
              />
            </div>
            {/* dotted line + marker */}
            <svg className="absolute -top-4 -left-10 w-24 h-20 text-brand-light/70" viewBox="0 0 120 90" fill="none" aria-hidden>
              <path d="M5 5 Q 5 60, 80 80" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 4" strokeLinecap="round" />
            </svg>
            <MapPin className="absolute -top-5 -left-11 h-5 w-5 text-brand-light fill-white" strokeWidth={2.4} />
          </div>
        </div>


        {/* header */}
        <div className="flex items-center gap-5 mb-2 relative z-10 min-h-[88px]">
          <div className="h-[72px] w-[72px] sm:h-20 sm:w-20 rounded-2xl gradient-brand flex items-center justify-center shadow-soft shrink-0">
            <Icon className="h-9 w-9 sm:h-10 sm:w-10 text-white" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <h3 className="text-[26px] sm:text-[30px] font-bold text-brand-dark leading-tight">{title}</h3>
            <span aria-hidden className="block mt-2 h-[3px] w-12 rounded-full bg-brand-light" />
          </div>
        </div>

        <p className="mt-6 text-[15px] sm:text-base text-muted-foreground leading-relaxed lg:max-w-[54%] relative z-10 lg:min-h-[96px]">
          {text}
        </p>

        <ul className="mt-7 lg:max-w-[54%] divide-y divide-[color:var(--border)]/70 relative z-10 flex-1">
          {benefits.map((b) => (
            <li key={b} className="flex items-center gap-3 py-3.5 text-[15px] text-foreground">
              <CheckCircle2 className="h-[18px] w-[18px] text-brand-light shrink-0" strokeWidth={2.4} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

      </div>

      <div className="px-10 sm:px-14 pb-10 relative z-10">
        <a
          href={WHATSAPP_LINK(msg)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${label} — abrir WhatsApp`}
          className="rne-cta-wa group/btn relative inline-flex items-center justify-center gap-3 w-full h-[52px] px-6 rounded-xl font-semibold text-[14.5px] tracking-[0.005em] text-white overflow-hidden transition-all duration-300 hover:-translate-y-[1px] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--whatsapp)]/50 focus-visible:ring-offset-2"
        >
          <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#1FBF5C] to-[#128C3D]" />
          <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-white/30" />
          <span aria-hidden className="absolute inset-0 ring-1 ring-inset ring-black/10 rounded-xl" />
          <span aria-hidden className="rne-cta-sheen pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/35 to-transparent skew-x-[-20deg]" />

          <span aria-hidden className="relative z-10 flex items-center justify-center">
            <span className="absolute inline-flex h-3 w-3 rounded-full bg-white/70 rne-cta-ping" />
            <WhatsAppIcon className="relative h-[22px] w-[22px] shrink-0" />
          </span>
          <span className="relative z-10 whitespace-nowrap">{label}</span>
          <ArrowRight className="relative z-10 h-[17px] w-[17px] opacity-80 rne-cta-arrow" strokeWidth={2.4} />
        </a>
        <p className="mt-4 flex items-center justify-center gap-2 text-[13px] text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-brand-light" />
          {footer}
        </p>
      </div>
    </div>
  );
}

function TrustStat({ icon: Icon, title, text }: { icon: typeof Box; title: string; text: string }) {
  return (
    <div className="flex items-start gap-4 px-6 py-2">
      <div className="h-11 w-11 rounded-xl bg-brand-light/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-brand-dark" strokeWidth={2} />
      </div>
      <div>
        <h4 className="text-[15px] font-bold text-brand-dark leading-tight">{title}</h4>
        <p className="mt-1 text-[13px] text-muted-foreground leading-snug">{text}</p>
      </div>
    </div>
  );
}

function HelpCenter() {
  const headRef = useReveal<HTMLDivElement>();
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const handler = () => {
      setFlash(false);
      requestAnimationFrame(() => setFlash(true));
      window.setTimeout(() => setFlash(false), 1400);
    };
    window.addEventListener("rne:highlight-help", handler);
    return () => window.removeEventListener("rne:highlight-help", handler);
  }, []);
  return (
    <section
      id={HELP_SECTION_ID}
      className={`scroll-mt-[100px] py-32 sm:py-44 relative overflow-hidden transition-shadow duration-700 ${
        flash ? "ring-4 ring-brand-light/50 ring-offset-0 shadow-[0_0_80px_0_rgba(37,211,102,0.35)_inset]" : ""
      }`}
    >
      <div className="relative mx-auto max-w-[1340px] px-4 sm:px-6 lg:px-8">
        <div ref={headRef} className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-light/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-dark">
            <Zap className="h-3.5 w-3.5 text-brand-light fill-brand-light" />
            Atendimento rápido
          </span>
          <h2 className="mt-5 text-4xl sm:text-5xl lg:text-[56px] font-extrabold text-brand-dark leading-[1.05] tracking-tight">
            Como podemos{" "}
            <span className="bg-gradient-to-r from-brand-light to-brand bg-clip-text text-transparent">
              ajudar você
            </span>
            {" "}hoje?
          </h2>
          <span aria-hidden className="mt-5 inline-block h-[3px] w-16 rounded-full bg-brand-light" />
          <p className="mt-5 mx-auto max-w-[640px] text-base sm:text-lg text-muted-foreground leading-relaxed">
            Escolha uma das opções abaixo e fale diretamente com nossa equipe. Assim conseguimos oferecer um atendimento mais rápido e personalizado.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
          <HelpCard
            icon={Box}
            title="Enviar Encomenda"
            text="Envie documentos, caixas, mercadorias e outros volumes com rapidez e segurança em toda a nossa rota."
            benefits={["Atendimento imediato", "Transporte seguro", "Coleta em cidades da rota", "Entrega rápida"]}
            label="Enviar Encomenda"
            msg={MESSAGE_ENCOMENDA}
            delay="0ms"
            image={boxesImg}
            imageAlt="Caixas Rota Norte Express"
            footer="Fale agora pelo WhatsApp e receba atendimento rápido!"
          />
          <HelpCard
            icon={Bus}
            title="Comprar Passagem"
            text="Consulte horários, disponibilidade e reserve sua viagem de forma rápida pelo WhatsApp."
            benefits={["Reserva rápida", "Atendimento personalizado", "Viagens pela BR-364 e BR-319", "Suporte imediato"]}
            label="Comprar Passagem"
            msg={MESSAGE_PASSAGEM}
            delay="120ms"
            image={vanImg}
            imageAlt="Chevrolet Onix Rota Norte Express"

            footer="Fale agora pelo WhatsApp e garanta sua viagem!"
          />
        </div>

        {/* Trust bar */}
        <div className="mt-10 bg-white rounded-3xl shadow-card border border-[color:var(--border)] p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 divide-y sm:divide-y-0 sm:divide-x divide-[color:var(--border)]/70">
          <TrustStat icon={ShieldCheck} title="Segurança em 1º lugar" text="Suas encomendas e viagens sempre protegidas." />
          <TrustStat icon={Clock} title="Pontualidade garantida" text="Cumprimos horários para você viajar e enviar sem preocupações." />
          <TrustStat icon={Headset} title="Atendimento humanizado" text="Nossa equipe está pronta para te atender com respeito e agilidade." />
          <TrustStat icon={MapPin} title="A maior cobertura da região" text="Conectamos você aos principais destinos da BR-364 e BR-319." />
        </div>

        {/* Canal alternativo: e-mail */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <p className="text-[14px] text-muted-foreground">
            Prefere e-mail? Use como canal alternativo para urgências ou orçamentos.
          </p>
          <a
            href="mailto:contato@rotanortexpress.com.br"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-border bg-white text-brand-dark font-semibold text-[14px] hover:border-brand-light/60 hover:-translate-y-0.5 transition-all shadow-soft"
          >
            <Mail className="h-4 w-4 text-brand-light" />
            Enviar e-mail
          </a>
        </div>
      </div>
    </section>
  );
}


/* ---------- DIFFERENTIALS ---------- */
function Differentials() {
  const items = [
    { i: ShieldCheck, t: "Segurança e confiança", d: "Frota cuidada e motoristas experientes em cada viagem." },
    { i: Zap, t: "Rapidez nas entregas", d: "Encomendas saem o quanto antes para chegar no prazo." },
    { i: HeartHandshake, t: "Atendimento humanizado", d: "Conversamos no seu ritmo e entendemos sua necessidade." },
    { i: Clock, t: "Pontualidade comprovada", d: "Horários respeitados em embarques e entregas diárias." },
    { i: MapIcon, t: "Cobertura regional", d: "Atendemos toda a rota Vilhena → Porto Velho → Manaus → Rio Branco." },
    { i: Users, t: "Equipe experiente", d: "Profissionais que conhecem cada quilômetro da BR-364 e BR-319." },
  ];
  const trust = [
    { i: Clock, t: "Saídas diárias", s: "com horários fixos" },
    { i: RouteIcon, t: "Rotas seguras", s: "e bem planejadas" },
    { i: Box, t: "Encomendas com", s: "seguro incluso" },
    { i: Headset, t: "Suporte rápido", s: "e eficiente" },
  ];
  return (
    <section id="diferenciais" className="relative py-20 sm:py-24">



      <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="eyebrow">Por que escolher</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold tracking-tight leading-[1.1]">
            <span className="block text-brand-dark">Por que escolher a</span>
            <span className="block text-brand-dark"><span className="text-brand">Rota Norte Express?</span></span>
          </h2>
          <span aria-hidden className="mt-5 inline-block h-[3px] w-14 rounded-full bg-[color:var(--accent-red)]" />
          <p className="mt-5 text-[15px] text-muted-foreground leading-relaxed">
            Confiança, agilidade e cuidado em cada viagem e cada entrega.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {items.map(({ i: Icon, t, d }) => (
            <div
              key={t}
              className="group relative bg-white rounded-2xl border border-[#E6ECF5] p-6 lg:p-7 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)] hover:shadow-[0_20px_45px_-22px_rgba(11,36,71,0.35)] transition-shadow duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-brand-dark to-brand shadow-[0_10px_24px_-10px_rgba(11,36,71,0.55)]">

                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <h3 className="font-display text-[17px] font-bold text-brand-dark leading-snug">{t}</h3>
                  <span aria-hidden className="mt-2 block h-[2px] w-10 rounded-full bg-[color:var(--accent-red)]/85" />
                  <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">{d}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust bar */}
        <div className="mt-12 lg:mt-14 rounded-2xl bg-gradient-to-r from-[#06122B] via-brand-dark to-[#0E2A55] px-5 sm:px-8 py-5 sm:py-6 shadow-[0_18px_45px_-22px_rgba(6,18,43,0.6)] border border-white/5">
          <ul className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
            {trust.map(({ i: Icon, t, s }, idx) => (
              <li key={t} className={`flex items-center gap-3 ${idx > 0 ? "lg:border-l lg:border-white/10 lg:pl-6" : ""}`}>
                <Icon className="h-7 w-7 text-brand-light shrink-0" strokeWidth={1.8} />
                <div className="text-white leading-tight">
                  <div className="text-[13px] sm:text-[14px] font-semibold">{t}</div>
                  <div className="text-[12px] sm:text-[13px] text-white/70">{s}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}


/* ---------- FAQ ---------- */
function FaqSection() {
  const items = FAQ_ITEMS;
  return (
    <section id="faq" className="py-20 sm:py-24">
      <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="eyebrow">Tire suas dúvidas</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
            Perguntas frequentes
          </h2>
          <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
            Reunimos as principais dúvidas de quem viaja e envia encomendas com a gente.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <article
              key={i}
              className="bg-white border border-border rounded-2xl p-6 shadow-soft hover:border-brand-light/40 transition-colors duration-200"
            >
              <h3 className="font-semibold text-brand-dark text-[15px] leading-snug">
                {it.q}
              </h3>
              <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">
                {it.a}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#06122B] via-brand-dark to-[#0E2A55] text-white py-28 sm:py-36">
      {/* Ambient glows */}
      <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-brand-light/15 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-[560px] h-[560px] rounded-full bg-brand/20 blur-[160px] pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-light/40 to-transparent pointer-events-none" />

      <div className="relative mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div className="text-center lg:text-left animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.08] border border-white/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-light">
            <Zap className="h-3.5 w-3.5 fill-brand-light" />
            Vamos começar
          </span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] font-extrabold leading-[1.05] tracking-tight">
            Pronto para viajar ou enviar uma{" "}
            <span className="bg-gradient-to-r from-brand-light to-white bg-clip-text text-transparent">
              encomenda?
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/75 max-w-[560px] mx-auto lg:mx-0 leading-relaxed">
            Nossa equipe está pronta para atender você com agilidade, segurança e atendimento humanizado em toda a BR-364 e BR-319.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <button
              type="button"
              onClick={scrollToHelp}
              className="group inline-flex items-center justify-center gap-3 h-[54px] px-7 rounded-xl bg-white text-brand-dark font-bold text-[15px] shadow-[0_20px_50px_-15px_rgba(255,255,255,0.4)] hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-15px_rgba(255,255,255,0.55)] transition-all"
            >
              <Headset className="h-5 w-5" />
              Solicitar Atendimento
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#cobertura"
              className="inline-flex items-center justify-center gap-2 h-[54px] px-6 rounded-xl border border-white/20 bg-white/[0.04] hover:bg-white/[0.10] text-white font-semibold text-[15px] transition-all"
            >
              <MapPin className="h-5 w-5 text-brand-light" />
              Ver rotas atendidas
            </a>
          </div>
        </div>

        <div className="relative animate-scale-in">
          <div className="absolute inset-0 rounded-[36px] bg-gradient-to-br from-brand-light/30 to-brand/10 blur-2xl" aria-hidden />
          <div className="relative rounded-[32px] overflow-hidden border border-white/15 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.7)]">
            <img
              src={fleetImg.url}
              alt="Frota Rota Norte Express — Fiat Fiorino e Chevrolet Spin na BR-364 e BR-319"
              loading="lazy"
              className="w-full h-[360px] sm:h-[440px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#06122B]/70 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-full bg-[color:var(--whatsapp)]/95 flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(37,211,102,0.6)]">
                  <WhatsAppIcon className="h-5 w-5 text-white" />
                </span>
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-white/70">Atendimento</div>
                  <div className="text-[15px] font-bold text-white">Resposta em minutos</div>
                </div>
              </div>
              <ShieldCheck className="h-7 w-7 text-brand-light" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}



function Footer() {
  const services = [
    "Transporte de Passageiros",
    "Envio de Encomendas",
    "Transporte de Documentos",
    "Atendimento para Empresas",
    "Coletas Programadas",
  ];
  return (
    <footer className="relative bg-[#07142A] text-white pt-24 pb-10 border-t border-white/5 overflow-hidden">
      {/* Premium ambient glows */}
      <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-brand-light/[0.08] blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 right-0 w-[520px] h-[520px] rounded-full bg-brand/[0.10] blur-[140px] pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-light/40 to-transparent pointer-events-none" />

      <div className="relative mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-10">
        <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
          <img src={logoAsset.url} alt="Rota Norte Express" width={150} height={100} className="h-11 w-auto" loading="lazy" decoding="async" />
          <p className="mt-6 text-white/65 text-[14px] leading-relaxed max-w-sm">
            Conectando Vilhena a Manaus e também a Rio Branco/AC, atendendo todas as cidades ao longo da BR-364 e BR-319 com segurança, agilidade e atendimento humanizado.
          </p>
          <div className="mt-7 flex gap-2.5 justify-center lg:justify-start">
            <a href="https://www.instagram.com/rotanortexpress/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 hover:bg-brand-light hover:border-brand-light hover:-translate-y-0.5 flex items-center justify-center transition-all">
              <Instagram className="h-[18px] w-[18px]" />
            </a>
            <a href="https://www.facebook.com/rotanortexpress/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 hover:bg-brand-light hover:border-brand-light hover:-translate-y-0.5 flex items-center justify-center transition-all">
              <Facebook className="h-[18px] w-[18px]" />
            </a>
            <button type="button" onClick={scrollToHelp} aria-label="Falar com a equipe" className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 hover:bg-brand-light hover:border-brand-light hover:-translate-y-0.5 flex items-center justify-center transition-all">
              <Headset className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        <div className="text-center lg:text-left">
          <h4 className="font-display font-bold text-[11px] uppercase tracking-[0.22em] text-brand-light/90">Serviços</h4>
          <ul className="mt-5 space-y-3 text-[14px]">
            {services.map((s) => (
              <li key={s} className="text-white/70">{s}</li>
            ))}
          </ul>
        </div>

        <div className="text-center lg:text-left">
          <h4 className="font-display font-bold text-[11px] uppercase tracking-[0.22em] text-brand-light/90">Contato</h4>
          <ul className="mt-5 space-y-3.5 text-[14px] text-white/70 inline-block lg:block text-left">
            <li className="flex items-center gap-2.5">
              <Phone className="h-4 w-4 text-brand-light shrink-0" />
              <a href={`tel:+${WHATSAPP_NUMBER}`} className="hover:text-white transition-colors">{WHATSAPP_DISPLAY}</a>
            </li>
            <li className="flex items-center gap-2.5">
              <Mail className="h-4 w-4 text-brand-light shrink-0" />
              <a href="mailto:contato@rotanortexpress.com.br" className="hover:text-white transition-colors">contato@rotanortexpress.com.br</a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 text-brand-light mt-0.5 shrink-0" />
              <span className="leading-relaxed">Av. Rio Madeira, 3288 — Embratel<br />Porto Velho/RO — CEP 76820-408</span>
            </li>
          </ul>
        </div>
      </div>


      <div className="relative mt-16 pt-6 border-t border-white/10 mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-[12px] text-white/45 text-center sm:text-left">
        <span>© {new Date().getFullYear()} Rota Norte Express. Todos os direitos reservados.</span>
        <nav aria-label="Links legais" className="flex items-center gap-4">
          <Link to="/termos" className="hover:text-white transition-colors">Termos de Uso</Link>
          <span aria-hidden className="h-3 w-px bg-white/20" />
          <Link to="/privacidade" className="hover:text-white transition-colors">Política de Privacidade</Link>
        </nav>
      </div>

    </footer>
  );
}


/* ---------- FLOATING WHATSAPP ---------- */
